import React, { useEffect, useState } from "react";
import {
  addDoc,
  getDocs,
  collection,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MarkdownRenderer from "../components/MarkdownRenderer";
import AnalyticsDashboard from "../components/AnalyticsDashboard";
import SubscribersList from "../components/Admin/SubscribersList";
import NewsletterComposer from "../components/Admin/NewsletterComposer";

// Toast Notification Component
const Toast = ({ message, type = "success", onClose }) => {
  const icons = {
    success: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    error: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    warning: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    info: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  };

  const colors = {
    success: {
      bg: "bg-white",
      border: "border-[#2E7D4F]/30",
      text: "text-[#2E7D4F]",
      icon: "text-[#2E7D4F]",
    },
    error: {
      bg: "bg-white",
      border: "border-[#C24E4E]/30",
      text: "text-[#C24E4E]",
      icon: "text-[#C24E4E]",
    },
    warning: {
      bg: "bg-white",
      border: "border-[#D9A32E]/40",
      text: "text-[#A8720E]",
      icon: "text-[#A8720E]",
    },
    info: {
      bg: "bg-white",
      border: "border-[#0F6BBF]/30",
      text: "text-[#0F6BBF]",
      icon: "text-[#0F6BBF]",
    },
  };

  const style = colors[type] || colors.info;

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
      className={`flex items-center gap-3 p-4 ${style.bg} rounded-xl border ${style.border} shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)] min-w-[300px] max-w-md`}
    >
      <div className={style.icon}>{icons[type]}</div>
      <p className={`flex-1 text-sm font-medium ${style.text}`}>{message}</p>
      <button
        onClick={onClose}
        className="text-[#5B6B7C] hover:text-[#26313B] transition-colors"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </motion.div>
  );
};

// Sidebar navigation item component
const NavItem = ({ icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
      active
        ? "bg-[#E8B44C]/20 text-[#26313B] border border-[#D9A32E]/40"
        : "text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5]"
    }`}
  >
    {icon}
    <span>{label}</span>
    {badge && (
      <span className="ml-auto px-2 py-0.5 text-xs rounded-full bg-[#E8B44C]/20 text-[#A8720E]">
        {badge}
      </span>
    )}
  </button>
);

// Stats card component
const StatCard = ({ icon, label, value, trend, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="relative group"
  >
    <div
      className={`absolute -inset-[1px] ${color} rounded-2xl opacity-0 group-hover:opacity-50 blur-sm transition-opacity`}
    />
    <div className="relative bg-white rounded-2xl p-5 border border-[#E1DCCE] shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)] hover:border-[#D9A32E]/40 transition-all">
      <div className="flex items-center justify-between mb-3">
        <div
          className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}
        >
          {icon}
        </div>
        {trend && (
          <span
            className={`text-xs font-medium ${
              trend > 0 ? "text-[#2E7D4F]" : "text-[#C24E4E]"
            }`}
          >
            {trend > 0 ? "+" : ""}
            {trend}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-[#26313B] mb-1">{value}</p>
      <p className="text-sm text-[#8A93A2]">{label}</p>
    </div>
  </motion.div>
);

// Category badge component
const CategoryBadge = ({ category }) => {
  const styles = {
    AI: "bg-[#E8B44C]/20 text-[#A8720E] border-[#D9A32E]/40",
    Technology: "bg-[#0F6BBF]/10 text-[#0F6BBF] border-[#0F6BBF]/30",
    Tutorial: "bg-[#2E7D4F]/10 text-[#2E7D4F] border-[#2E7D4F]/30",
    Update: "bg-[#A8720E]/10 text-[#A8720E] border-[#A8720E]/30",
  };
  return (
    <span
      className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border ${
        styles[category] || styles.Technology
      }`}
    >
      {category}
    </span>
  );
};

export const BlogAdmin = () => {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [category, setCategory] = useState("");
  const [prompt, setPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [activeView, setActiveView] = useState("editor");
  const [searchQuery, setSearchQuery] = useState("");
  const [isPremium, setIsPremium] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const itemsPerPage = 5;
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();

  // Toast notification helper
  const showToast = (message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 4000);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Failed to logout", error);
    }
  };

  // Upload image to Firebase Storage
  const handleImageUpload = async (file) => {
    if (!file) return null;

    setUploading(true);
    try {
      const timestamp = Date.now();
      const storageRef = ref(storage, `blog-images/${timestamp}-${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Insert markdown image syntax at cursor position
      const imageMarkdown = `\n![${file.name}](${downloadURL})\n`;
      setText((prev) => prev + imageMarkdown);

      showToast("Image uploaded successfully!", "success");
      return downloadURL;
    } catch (error) {
      console.error("Error uploading image:", error);
      showToast("Failed to upload image", "error");
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Add or Update Blog
  const handleAdd = async () => {
    if (!title.trim() || !text.trim() || !category.trim()) {
      showToast("Please fill in title, content, and category", "warning");
      return;
    }

    // Word count validation
    const wordCount = text.trim().split(/\s+/).length;
    if (wordCount < 200) {
      if (
        !window.confirm(
          `Your blog has only ${wordCount} words. It's recommended to have at least 400-500 words for better engagement. Continue anyway?`
        )
      ) {
        return;
      }
    }

    showToast(
      editId ? "Updating blog post..." : "Publishing blog post...",
      "info"
    );

    try {
      // If marking as featured, unfeatured all other blogs first
      if (isFeatured) {
        const allBlogs = await getDocs(collection(db, "Blogs"));
        const updatePromises = allBlogs.docs
          .filter((docItem) => docItem.id !== editId) // Don't update the current blog
          .map((docItem) =>
            updateDoc(doc(db, "Blogs", docItem.id), { isFeatured: false })
          );
        await Promise.all(updatePromises);
      }

      if (editId) {
        const ref = doc(db, "Blogs", editId);
        await updateDoc(ref, {
          blog_title: title,
          blog_contetnt: text,
          blog_category: category,
          isPremium: isPremium,
          isFeatured: isFeatured,
          updatedAt: Date.now(),
        });

        setItems((prev) =>
          prev.map((item) =>
            item.firestoreId === editId
              ? { ...item, title, text, category, isPremium, isFeatured }
              : isFeatured
              ? { ...item, isFeatured: false }
              : item
          )
        );
        setEditId(null);
      } else {
        const ref = await addDoc(collection(db, "Blogs"), {
          blog_title: title,
          blog_contetnt: text,
          blog_category: category,
          isPremium: isPremium,
          isFeatured: isFeatured,
          createdAt: Date.now(),
        });

        setItems([
          {
            firestoreId: ref.id,
            title,
            text,
            category,
            isPremium,
            isFeatured,
            createdAt: Date.now(),
          },
          ...items.map((item) => (isFeatured ? { ...item, isFeatured: false } : item)),
        ]);
      }

      setTitle("");
      setText("");
      setCategory("");
      setIsPremium(false);
      setIsFeatured(false);
      setShowPreview(false);
      showToast(
        editId ? "Blog updated successfully!" : "Blog published successfully!",
        "success"
      );
    } catch (error) {
      console.error("Error saving blog:", error);
      showToast("Failed to save blog post", "error");
    }
  };

  // Fetch all items
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const snapshot = await getDocs(collection(db, "Blogs"));
        const list = snapshot.docs.map((docItem) => ({
          firestoreId: docItem.id,
          title: docItem.data().blog_title || "",
          text: docItem.data().blog_contetnt,
          category: docItem.data().blog_category || "Uncategorized",
          createdAt: docItem.data().createdAt || Date.now(),
          isPremium: docItem.data().isPremium || false,
          isFeatured: docItem.data().isFeatured || false,
        }));
        list.sort((a, b) => b.createdAt - a.createdAt);
        setItems(list);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
    };

    fetchItems();
  }, []);

  // Fetch subscriber count
  useEffect(() => {
    const fetchSubscriberCount = async () => {
      try {
        const usersQuery = query(
          collection(db, "Users"),
          where("subscribedToNewsletter", "==", true),
          where("isUnsubscribed", "==", false)
        );
        const snapshot = await getDocs(usersQuery);
        setSubscriberCount(snapshot.size);
      } catch (error) {
        console.error("Error fetching subscriber count:", error);
      }
    };

    fetchSubscriberCount();
  }, []);

  const handleEdit = (item) => {
    setTitle(item.title || "");
    setText(item.text);
    setCategory(item.category);
    setIsPremium(item.isPremium || false);
    setIsFeatured(item.isFeatured || false);
    setEditId(item.firestoreId);
    setActiveView("editor");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (firestoreId) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;

    try {
      await deleteDoc(doc(db, "Blogs", firestoreId));
      setItems((prev) =>
        prev.filter((item) => item.firestoreId !== firestoreId)
      );
      showToast("Blog deleted successfully", "success");
    } catch (err) {
      console.error("Error deleting:", err);
      showToast("Failed to delete blog", "error");
    }
  };

  const handleToggleFeatured = async (firestoreId, currentFeaturedStatus) => {
    try {
      const newFeaturedStatus = !currentFeaturedStatus;

      // If setting as featured, unfeatured all other blogs first
      if (newFeaturedStatus) {
        const allBlogs = await getDocs(collection(db, "Blogs"));
        const updatePromises = allBlogs.docs
          .filter((docItem) => docItem.id !== firestoreId)
          .map((docItem) =>
            updateDoc(doc(db, "Blogs", docItem.id), { isFeatured: false })
          );
        await Promise.all(updatePromises);
      }

      // Update the current blog
      await updateDoc(doc(db, "Blogs", firestoreId), {
        isFeatured: newFeaturedStatus,
      });

      // Update local state
      setItems((prev) =>
        prev.map((item) =>
          item.firestoreId === firestoreId
            ? { ...item, isFeatured: newFeaturedStatus }
            : newFeaturedStatus
            ? { ...item, isFeatured: false }
            : item
        )
      );

      showToast(
        newFeaturedStatus
          ? "Blog marked as featured!"
          : "Blog removed from featured",
        "success"
      );
    } catch (error) {
      console.error("Error toggling featured:", error);
      showToast("Failed to update featured status", "error");
    }
  };

  const handleCancel = () => {
    setTitle("");
    setText("");
    setCategory("");
    setPrompt("");
    setIsPremium(false);
    setIsFeatured(false);
    setEditId(null);
    setShowPreview(false);
  };

  // Calculate word count and reading time
  const getWordCount = () =>
    text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  const getReadingTime = () => Math.ceil(getWordCount() / 200);
  const getWordCountStatus = () => {
    const count = getWordCount();
    if (count < 300) return { color: "#C24E4E", label: "Too short" };
    if (count < 400) return { color: "#A8720E", label: "Short" };
    if (count >= 400 && count <= 600)
      return { color: "#2E7D4F", label: "Optimal" };
    if (count <= 800) return { color: "#0F6BBF", label: "Good" };
    return { color: "#5B6B7C", label: "Long" };
  };

  // Generate blog content with AI
  const handleGenerateWithAI = async () => {
    if (!prompt.trim()) {
      showToast("Please enter a prompt/idea for your blog post", "warning");
      return;
    }

    setAiGenerating(true);
    showToast("Generating blog with AI...", "info");

    try {
      // Call the Cloud Function
      const response = await fetch(
        "https://us-central1-cosmovex.cloudfunctions.net/generateBlogWithAI",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt: prompt,
            category: category || undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error:", errorText);
        throw new Error("Failed to generate blog content");
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Invalid response format from server");
      }

      let data = await response.json();

      console.log("========== AI RESPONSE DEBUG ==========");
      console.log("Raw response:", JSON.stringify(data, null, 2));
      console.log("Response type:", typeof data);
      console.log("Response keys:", data ? Object.keys(data) : "no keys");
      console.log("======================================");

      // Try to unwrap the response if it's nested
      let unwrappedData = data;

      // Check multiple levels of nesting
      if (data?.result) {
        console.log("Found data.result, unwrapping...");
        unwrappedData = data.result;
      } else if (data?.data) {
        console.log("Found data.data, unwrapping...");
        unwrappedData = data.data;
      } else if (data?.response) {
        console.log("Found data.response, unwrapping...");
        unwrappedData = data.response;
      }

      // Handle if the unwrapped data is a string (double-stringified JSON)
      if (typeof unwrappedData === "string") {
        console.log("Data is string, attempting to parse...");
        console.log("String content:", unwrappedData.substring(0, 200));

        // Remove markdown code block markers if present (```json ... ``` or ``` ... ```)
        let cleanedString = unwrappedData.trim();
        if (cleanedString.startsWith("```json")) {
          console.log("Removing ```json wrapper...");
          cleanedString = cleanedString
            .replace(/^```json\s*\n?/, "")
            .replace(/\n?```\s*$/, "");
        } else if (cleanedString.startsWith("```")) {
          console.log("Removing ``` wrapper...");
          cleanedString = cleanedString
            .replace(/^```\s*\n?/, "")
            .replace(/\n?```\s*$/, "");
        }

        try {
          unwrappedData = JSON.parse(cleanedString);
          console.log("Successfully parsed string to object");
        } catch (e) {
          console.error("Failed to parse as JSON, treating as plain text");
          // If it's not JSON, treat it as markdown content
          const lines = cleanedString.split("\n");
          const firstLine =
            lines[0]?.replace(/^#+\s*/, "").trim() || "AI Generated Blog";
          const restContent = lines.slice(1).join("\n").trim();

          setTitle(firstLine);
          setText(restContent || cleanedString);
          setCategory(category || "Technology");
          setPrompt("");
          showToast("Content extracted from text format", "success");
          setAiGenerating(false);
          return;
        }
      }

      // Now extract fields with all possible variations
      console.log("Extracting fields from:", unwrappedData);

      let blogTitle = "";
      let blogContent = "";
      let blogCategory = "";

      // Try all possible field name variations for title
      const titleFields = [
        "title",
        "blog_title",
        "blogTitle",
        "heading",
        "name",
        "post_title",
      ];
      for (const field of titleFields) {
        if (unwrappedData[field]) {
          blogTitle = String(unwrappedData[field]).trim();
          console.log(
            `Found title in field '${field}':`,
            blogTitle.substring(0, 50)
          );
          break;
        }
      }

      // Try all possible field name variations for content
      const contentFields = [
        "content",
        "blog_content",
        "blogContent",
        "blog_contetnt",
        "text",
        "body",
        "markdown",
        "post_content",
      ];
      for (const field of contentFields) {
        if (unwrappedData[field]) {
          blogContent = String(unwrappedData[field]).trim();
          console.log(
            `Found content in field '${field}', length:`,
            blogContent.length
          );
          break;
        }
      }

      // Try all possible field name variations for category
      const categoryFields = [
        "category",
        "blog_category",
        "blogCategory",
        "tag",
        "type",
      ];
      for (const field of categoryFields) {
        if (unwrappedData[field]) {
          blogCategory = String(unwrappedData[field]).trim();
          console.log(`Found category in field '${field}':`, blogCategory);
          break;
        }
      }

      // Fallback to original category if not found
      if (!blogCategory) {
        blogCategory = category || "Technology";
      }

      console.log("========== EXTRACTION RESULTS ==========");
      console.log("Title:", blogTitle);
      console.log("Content length:", blogContent.length);
      console.log("Category:", blogCategory);
      console.log("=======================================");

      // If we still don't have content, show all available data
      if (!blogTitle && !blogContent) {
        console.error("Could not extract any data!");
        console.error("Available fields:", Object.keys(unwrappedData));
        console.error("Full data:", JSON.stringify(unwrappedData, null, 2));

        showToast(
          `Could not parse AI response. Available fields: ${Object.keys(
            unwrappedData
          ).join(", ")}. Check console for details.`,
          "error"
        );
        throw new Error("Generated content is incomplete or in wrong format");
      }

      // Populate the form with AI-generated content
      setTitle(blogTitle);
      setText(blogContent);
      setCategory(blogCategory);

      // Clear the prompt
      setPrompt("");

      // Show a success message
      showToast(
        "Blog content generated successfully! Review and edit as needed.",
        "success"
      );
    } catch (error) {
      console.error("Error generating blog:", error);
      showToast("Failed to generate blog content. Please try again.", "error");
    } finally {
      setAiGenerating(false);
    }
  };

  // Filter items based on search
  const filteredItems = items.filter((item) => {
    const itemTitle = (item.title || "").toLowerCase();
    const content = item.text.toLowerCase();
    const cat = item.category.toLowerCase();
    const query = searchQuery.toLowerCase();
    return (
      itemTitle.includes(query) ||
      content.includes(query) ||
      cat.includes(query)
    );
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  // Category stats
  const categoryStats = items.reduce((acc, item) => {
    acc[item.category] = (acc[item.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#3D4B59] flex">
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
        <AnimatePresence>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() =>
                setToasts((prev) => prev.filter((t) => t.id !== toast.id))
              }
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-[#E1DCCE] p-4 flex flex-col z-50">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 px-2">
          <div className="w-10 h-10 bg-[#E8B44C] border border-[#D9A32E] rounded-xl flex items-center justify-center">
            <span className="text-lg font-black text-[#221A06]">C</span>
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#26313B]">Cosmovex</h1>
            <p className="text-xs text-[#8A93A2] font-mono">// admin</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="space-y-1 flex-1">
          <NavItem
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            }
            label="Editor"
            active={activeView === "editor"}
            onClick={() => setActiveView("editor")}
          />
          <NavItem
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            }
            label="All Posts"
            active={activeView === "posts"}
            onClick={() => setActiveView("posts")}
            badge={items.length}
          />
          <NavItem
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            }
            label="Subscribers"
            active={activeView === "subscribers"}
            onClick={() => setActiveView("subscribers")}
            badge={subscriberCount}
          />
          <NavItem
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            }
            label="Newsletter"
            active={activeView === "newsletter"}
            onClick={() => setActiveView("newsletter")}
          />
          <NavItem
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            }
            label="Analytics"
            active={activeView === "analytics"}
            onClick={() => setActiveView("analytics")}
          />
        </nav>

        {/* User section */}
        <div className="border-t border-[#E1DCCE] pt-4 mt-4">
          <div className="flex items-center gap-3 px-2 mb-4">
            <div className="w-9 h-9 rounded-full bg-[#E8B44C] text-[#221A06] flex items-center justify-center text-sm font-bold">
              {currentUser?.email?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#26313B] truncate">
                {currentUser?.email}
              </p>
              <p className="text-xs text-[#8A93A2]">Administrator</p>
            </div>
          </div>
          <div className="space-y-1">
            <button
              onClick={() => navigate("/blogs")}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] transition-all"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              View Blog
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#C24E4E] hover:text-[#C24E4E] hover:bg-[#C24E4E]/10 transition-all"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-[#FBFAF6]/80 backdrop-blur-xl border-b border-[#E1DCCE]">
          <div className="px-8 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#26313B]">
                {activeView === "editor"
                  ? editId
                    ? "Edit Post"
                    : "Create Post"
                  : activeView === "subscribers"
                  ? "Subscribers"
                  : activeView === "newsletter"
                  ? "Newsletter"
                  : activeView === "analytics"
                  ? "Analytics"
                  : "All Posts"}
              </h1>
              <p className="text-sm text-[#8A93A2] mt-1">
                {activeView === "editor"
                  ? "Write and publish your content"
                  : activeView === "subscribers"
                  ? `Managing ${subscriberCount} active subscribers`
                  : activeView === "newsletter"
                  ? "Send newsletters to your subscribers"
                  : activeView === "analytics"
                  ? "Track your blog performance and engagement"
                  : `Managing ${items.length} posts`}
              </p>
            </div>
            {activeView === "posts" && (
              <button
                onClick={() => {
                  setActiveView("editor");
                  handleCancel();
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#E8B44C] text-[#221A06] border border-[#D9A32E] font-medium rounded-xl hover:bg-[#D9A32E] transition-all"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                New Post
              </button>
            )}
          </div>
        </header>

        <div className="p-8">
          <AnimatePresence mode="wait">
            {activeView === "editor" ? (
              <motion.div
                key="editor"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Stats Row */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  <StatCard
                    icon={
                      <svg
                        className="w-5 h-5 text-[#A8720E]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    }
                    label="Total Posts"
                    value={items.length}
                    color="bg-[#E8B44C]/20"
                  />
                  <StatCard
                    icon={
                      <svg
                        className="w-5 h-5 text-[#A8720E]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    }
                    label="AI Posts"
                    value={categoryStats["AI"] || 0}
                    color="bg-[#E8B44C]/20"
                  />
                  <StatCard
                    icon={
                      <svg
                        className="w-5 h-5 text-[#A8720E]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                        />
                      </svg>
                    }
                    label="Tech Posts"
                    value={categoryStats["Technology"] || 0}
                    color="bg-[#0F6BBF]/10"
                  />
                  <StatCard
                    icon={
                      <svg
                        className="w-5 h-5 text-[#A8720E]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                    }
                    label="Tutorials"
                    value={categoryStats["Tutorial"] || 0}
                    color="bg-[#2E7D4F]/10"
                  />
                </div>

                {/* Editor Card */}
                <div className="relative">
                  <div className="absolute -inset-[1px] bg-[#E8B44C]/20 rounded-3xl opacity-50 blur-sm" />
                  <div className="relative bg-white rounded-3xl border border-[#E1DCCE] shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)] overflow-hidden">
                    {/* Editor Header */}
                    <div className="flex items-center justify-between p-6 border-b border-[#E1DCCE]">
                      <div className="flex items-center gap-4">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500/80" />
                          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                          <div className="w-3 h-3 rounded-full bg-green-500/80" />
                        </div>
                        <span className="text-sm text-[#8A93A2] font-mono">
                          {editId ? "editing-post.md" : "new-post.md"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowPreview(!showPreview)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            showPreview
                              ? "bg-[#E8B44C]/20 text-[#A8720E] border border-[#D9A32E]/40"
                              : "text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5]"
                          }`}
                        >
                          {showPreview ? "Hide Preview" : "Preview"}
                        </button>
                      </div>
                    </div>

                    {/* Editor Body */}
                    <div className="p-6 space-y-6">
                      {/* AI Generation Section */}
                      <div className="p-5 bg-[#F1EEE5] rounded-xl border border-[#E1DCCE]">
                        <div className="flex items-center gap-2 mb-3">
                          <svg
                            className="w-5 h-5 text-[#A8720E]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                          </svg>
                          <span className="text-sm font-semibold text-[#A8720E]">
                            AI Blog Generator
                          </span>
                          <span className="ml-auto text-xs text-[#A8720E]/60 font-mono">
                            Powered by Gemini
                          </span>
                        </div>
                        <p className="text-xs text-[#5B6B7C] mb-3">
                          Describe your blog idea and let AI generate a complete
                          blog post with title, formatting, and content
                        </p>
                        <div className="space-y-3">
                          <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="Example: Write a comprehensive blog post about the benefits of using AI in modern web development, include code examples and best practices..."
                            className="w-full px-4 py-3 bg-white border border-[#E1DCCE] rounded-xl text-[#26313B] placeholder-[#8A93A2] focus:outline-none focus:border-[#D9A32E] transition-all text-sm resize-none"
                            rows={3}
                            disabled={aiGenerating}
                          />
                          <button
                            onClick={handleGenerateWithAI}
                            disabled={aiGenerating || !prompt.trim()}
                            className="w-full relative group disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <div className="absolute -inset-[1px] bg-[#E8B44C]/40 rounded-xl opacity-75 group-hover:opacity-100 blur-sm transition-opacity" />
                            <div className="relative px-4 py-2.5 bg-[#E8B44C] text-[#221A06] border border-[#D9A32E] font-medium rounded-xl flex items-center justify-center gap-2">
                              {aiGenerating ? (
                                <>
                                  <svg
                                    className="w-4 h-4 animate-spin"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                  </svg>
                                  Generating with AI...
                                </>
                              ) : (
                                <>
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13 10V3L4 14h7v7l9-11h-7z"
                                    />
                                  </svg>
                                  Generate Blog with AI
                                </>
                              )}
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Category Select */}
                      <div>
                        <label className="block text-sm font-medium text-[#5B6B7C] mb-2 flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-[#A8720E]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                            />
                          </svg>
                          Category
                        </label>
                        <div className="relative">
                          <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full px-4 py-3.5 bg-white border border-[#E1DCCE] rounded-xl text-[#26313B] focus:outline-none focus:border-[#D9A32E] transition-all appearance-none cursor-pointer"
                          >
                            <option value="">Select Category</option>
                            <option value="AI">AI</option>
                            <option value="Technology">Technology</option>
                            <option value="Tutorial">Tutorial</option>
                            <option value="Update">Update</option>
                          </select>
                          <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg
                              className="w-5 h-5 text-[#8A93A2]"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Title Input */}
                      <div>
                        <label className="block text-sm font-medium text-[#5B6B7C] mb-2 flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-[#A8720E]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                            />
                          </svg>
                          Title
                        </label>
                        <input
                          type="text"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Enter blog post title..."
                          className="w-full px-4 py-3.5 bg-white border border-[#E1DCCE] rounded-xl text-[#26313B] placeholder-[#8A93A2] focus:outline-none focus:border-[#D9A32E] transition-all"
                        />
                      </div>

                      {/* Premium Content Toggle */}
                      <div>
                        <label className="flex items-center justify-between p-4 bg-[#E8B44C]/10 rounded-xl border border-[#D9A32E]/30 cursor-pointer hover:border-[#D9A32E]/60 transition-all">
                          <div className="flex items-center gap-3">
                            <svg
                              className="w-5 h-5 text-[#A8720E]"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <div>
                              <p className="text-sm font-semibold text-[#26313B]">
                                Premium Content
                              </p>
                              <p className="text-xs text-[#5B6B7C]">
                                Mark this article as premium/exclusive
                              </p>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={isPremium}
                            onChange={(e) => setIsPremium(e.target.checked)}
                            className="w-5 h-5 rounded bg-white border-[#D9A32E]/40 text-[#A8720E] focus:ring-2 focus:ring-[#D9A32E]/50"
                          />
                        </label>
                      </div>

                      {/* Featured Blog Toggle */}
                      <div>
                        <label className="flex items-center justify-between p-4 bg-[#E8B44C]/10 rounded-xl border border-[#D9A32E]/30 cursor-pointer hover:border-[#D9A32E]/60 transition-all">
                          <div className="flex items-center gap-3">
                            <svg
                              className="w-5 h-5 text-[#A8720E]"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <div>
                              <p className="text-sm font-semibold text-[#26313B]">
                                Featured Blog
                              </p>
                              <p className="text-xs text-[#5B6B7C]">
                                Display this blog in the featured section (only one can be featured)
                              </p>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={isFeatured}
                            onChange={(e) => setIsFeatured(e.target.checked)}
                            className="w-5 h-5 rounded bg-white border-[#D9A32E]/40 text-[#A8720E] focus:ring-2 focus:ring-[#D9A32E]/50"
                          />
                        </label>
                      </div>

                      {/* Image Upload */}
                      <div>
                        <label className="block text-sm font-medium text-[#5B6B7C] mb-2 flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-[#A8720E]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          Add Images
                        </label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleImageUpload(file);
                                e.target.value = ""; // Reset input
                              }
                            }}
                            disabled={uploading}
                            className="hidden"
                            id="image-upload"
                          />
                          <label
                            htmlFor="image-upload"
                            className={`flex items-center justify-center gap-2 px-4 py-3 bg-white border border-[#E1DCCE] rounded-xl text-[#A8720E] hover:bg-[#F1EEE5] hover:border-[#D9A32E] transition-all cursor-pointer ${
                              uploading ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                          >
                            {uploading ? (
                              <>
                                <svg
                                  className="w-5 h-5 animate-spin"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                                Uploading image...
                              </>
                            ) : (
                              <>
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                  />
                                </svg>
                                Upload Image
                              </>
                            )}
                          </label>
                        </div>
                        <p className="text-xs text-[#8A93A2] mt-2">
                          Images will be uploaded to Firebase Storage and
                          inserted as markdown
                        </p>
                      </div>

                      {/* Markdown Editor */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="flex items-center gap-2 text-sm font-medium text-[#5B6B7C]">
                            <svg
                              className="w-4 h-4 text-[#A8720E]"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            Content (Markdown)
                          </label>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-[#8A93A2]">
                                {getWordCount()} words
                              </span>
                              <span
                                className="px-2 py-0.5 text-xs font-semibold rounded-full"
                                style={{
                                  backgroundColor: `${
                                    getWordCountStatus().color
                                  }20`,
                                  color: getWordCountStatus().color,
                                  border: `1px solid ${
                                    getWordCountStatus().color
                                  }40`,
                                }}
                              >
                                {getWordCountStatus().label}
                              </span>
                              <span className="text-xs text-[#8A93A2] font-mono">
                                • {getReadingTime()} min read
                              </span>
                            </div>
                          </div>
                        </div>
                        {getWordCount() > 0 && getWordCount() < 400 && (
                          <div className="mb-3 p-3 bg-[#E8B44C]/10 border border-[#D9A32E]/30 rounded-lg flex items-start gap-2">
                            <svg
                              className="w-5 h-5 text-[#A8720E] flex-shrink-0 mt-0.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            <div>
                              <p className="text-sm text-[#A8720E] font-medium">
                                Recommended: 400-500 words
                              </p>
                              <p className="text-xs text-[#5B6B7C] mt-0.5">
                                Your blog is currently {getWordCount()} words.
                                Aim for 400-500 words for better engagement and
                                SEO.
                              </p>
                            </div>
                          </div>
                        )}
                        <textarea
                          value={text}
                          onChange={(e) => setText(e.target.value)}
                          placeholder="Write your blog post in markdown...&#10;&#10;## Heading&#10;**Bold text** and *italic text*&#10;&#10;- List item 1&#10;- List item 2&#10;&#10;[Link text](https://example.com)&#10;&#10;Paste YouTube URLs and they'll become embeds!"
                          className="w-full px-4 py-3.5 bg-[#F1EEE5] border border-[#E1DCCE] rounded-xl text-[#26313B] placeholder-[#8A93A2] focus:outline-none focus:border-[#D9A32E] transition-all font-mono text-sm resize-none"
                          rows={18}
                        />
                      </div>

                      {/* Preview */}
                      <AnimatePresence>
                        {showPreview && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 bg-white rounded-xl border border-[#E1DCCE]">
                              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-[#E1DCCE]">
                                <svg
                                  className="w-4 h-4 text-[#A8720E]"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                                <span className="text-sm font-medium text-[#5B6B7C]">
                                  Live Preview
                                </span>
                              </div>
                              {title && (
                                <h1 className="text-3xl font-bold text-[#26313B] mb-6">
                                  {title}
                                </h1>
                              )}
                              <MarkdownRenderer content={text} />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Editor Footer */}
                    <div className="flex items-center justify-between p-6 border-t border-[#E1DCCE] bg-[#F1EEE5]/50">
                      <div className="flex items-center gap-4 text-sm">
                        {getWordCount() > 0 && (
                          <>
                            <span className="text-[#8A93A2]">
                              {getWordCount()} words • {text.length} characters
                            </span>
                            <span
                              className="px-2 py-1 text-xs font-semibold rounded-full"
                              style={{
                                backgroundColor: `${
                                  getWordCountStatus().color
                                }20`,
                                color: getWordCountStatus().color,
                                border: `1px solid ${
                                  getWordCountStatus().color
                                }40`,
                              }}
                            >
                              {getWordCountStatus().label}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex gap-3">
                        {editId && (
                          <button
                            onClick={handleCancel}
                            className="px-5 py-2.5 text-sm font-medium text-[#5B6B7C] hover:text-[#26313B] bg-[#F1EEE5] hover:bg-[#E1DCCE]/60 rounded-xl transition-all"
                          >
                            Cancel
                          </button>
                        )}
                        <button onClick={handleAdd} className="relative group">
                          <div className="absolute -inset-[1px] bg-[#E8B44C]/40 rounded-xl opacity-75 group-hover:opacity-100 blur-sm transition-opacity" />
                          <div className="relative px-6 py-2.5 bg-[#E8B44C] text-[#221A06] border border-[#D9A32E] font-medium rounded-xl flex items-center gap-2">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d={editId ? "M5 13l4 4L19 7" : "M12 4v16m8-8H4"}
                              />
                            </svg>
                            {editId ? "Update Post" : "Publish Post"}
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : activeView === "subscribers" ? (
              <motion.div
                key="subscribers"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <SubscribersList />
              </motion.div>
            ) : activeView === "newsletter" ? (
              <motion.div
                key="newsletter"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <NewsletterComposer />
              </motion.div>
            ) : activeView === "posts" ? (
              <motion.div
                key="posts"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Search */}
                <div className="mb-6">
                  <div className="relative max-w-md">
                    <svg
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A93A2]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search posts..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-white border border-[#E1DCCE] rounded-xl text-[#26313B] placeholder-[#8A93A2] focus:outline-none focus:border-[#D9A32E] transition-all"
                    />
                  </div>
                </div>

                {/* Posts List */}
                <div className="space-y-4">
                  {currentItems.map((item, idx) => (
                    <motion.div
                      key={item.firestoreId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="group relative"
                    >
                      <div className="absolute -inset-[1px] bg-[#E8B44C]/20 rounded-2xl opacity-0 group-hover:opacity-100 blur-sm transition-all" />
                      <div className="relative bg-white rounded-2xl border border-[#E1DCCE] shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)] group-hover:border-[#D9A32E]/50 p-6 transition-all">
                        <div className="flex items-start justify-between gap-6">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-3">
                              <CategoryBadge category={item.category} />
                              {item.isFeatured && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full border bg-[#E8B44C]/20 text-[#A8720E] border-[#D9A32E]/40">
                                  <svg
                                    className="w-3 h-3"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                  Featured
                                </span>
                              )}
                              <span className="text-xs text-[#8A93A2] font-mono">
                                {new Date(item.createdAt).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  }
                                )}
                              </span>
                            </div>
                            <h3 className="text-lg font-semibold text-[#26313B] mb-2 line-clamp-1">
                              {item.title || "Untitled Post"}
                            </h3>
                            <p className="text-sm text-[#5B6B7C] line-clamp-2">
                              {item.text.substring(0, 150)}...
                            </p>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() =>
                                handleToggleFeatured(
                                  item.firestoreId,
                                  item.isFeatured
                                )
                              }
                              className={`p-2.5 rounded-xl transition-all ${
                                item.isFeatured
                                  ? "bg-[#E8B44C]/20 text-[#A8720E] hover:bg-[#E8B44C]/30"
                                  : "bg-[#F1EEE5] hover:bg-[#E8B44C]/20 text-[#5B6B7C] hover:text-[#A8720E]"
                              }`}
                              title={
                                item.isFeatured
                                  ? "Remove from featured"
                                  : "Mark as featured"
                              }
                            >
                              <svg
                                className="w-4 h-4"
                                fill={item.isFeatured ? "currentColor" : "none"}
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2.5 rounded-xl bg-[#F1EEE5] hover:bg-[#0F6BBF]/10 text-[#5B6B7C] hover:text-[#0F6BBF] transition-all"
                              title="Edit"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(item.firestoreId)}
                              className="p-2.5 rounded-xl bg-[#F1EEE5] hover:bg-[#C24E4E]/10 text-[#5B6B7C] hover:text-[#C24E4E] transition-all"
                              title="Delete"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Empty State */}
                {filteredItems.length === 0 && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#E8B44C]/15 flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-[#A8720E]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <p className="text-[#8A93A2] mb-2">No posts found</p>
                    <p className="text-sm text-[#8A93A2]">
                      Try a different search or create a new post
                    </p>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                      className="w-10 h-10 rounded-xl bg-white hover:bg-[#F1EEE5] border border-[#E1DCCE] flex items-center justify-center transition-all disabled:opacity-30"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (num) => (
                        <button
                          key={num}
                          onClick={() => setCurrentPage(num)}
                          className={`w-10 h-10 rounded-xl font-medium transition-all ${
                            currentPage === num
                              ? "bg-[#E8B44C] text-[#221A06] border border-[#D9A32E]"
                              : "bg-white text-[#5B6B7C] hover:bg-[#F1EEE5] border border-[#E1DCCE]"
                          }`}
                        >
                          {num}
                        </button>
                      )
                    )}
                    <button
                      onClick={() =>
                        setCurrentPage(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="w-10 h-10 rounded-xl bg-white hover:bg-[#F1EEE5] border border-[#E1DCCE] flex items-center justify-center transition-all disabled:opacity-30"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </motion.div>
            ) : activeView === "analytics" ? (
              <motion.div
                key="analytics"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <AnalyticsDashboard />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};
